// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import Image from '@tcp/core/src/components/common/atoms/Image';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Row from '@tcp/core/src/components/common/atoms/Row';
import Col from '@tcp/core/src/components/common/atoms/Col';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import DamImage from '@tcp/core/src/components/common/atoms/DamImage';
import { getIconPath, getLocator, getAPIConfig, getBrand } from '@tcp/core/src/utils';
import { getUpdatedSizeName, parseBoolean } from '@tcp/core/src/utils/utils';
import styles from '../styles/OrderItem.style';
import getItemQuantity, { getPdpAsPathurl, getPdpToPath } from './OrderItem.view.utils';
import Button from '../../../../../../common/atoms/Button';
import {
  getLabelFromCopyOrFromTranslation,
  getLabelWithDefaultValue,
} from '../../../../../../../utils/labels/labelUtilty';
import {
  ACCOUNT_ORDERS_LABEL,
  CHECKOUT_BAGPAGE_LABEL,
  ORDER_DETAILS_PREORDER,
  OrderDetailLabels,
  lblOrderDetailsColor,
  lblOrderDetailsFit,
  lblOrderDetailsPrice,
  lblOrderDetailsSize,
  lblOrdersAdjustment,
  lblOtemNumber,
  lblPreOrderTag,
  lblQuantity,
  lblSubTotal,
  lblWriteReview,
  lblYouPaid,
} from '../../../container/OrderDetails.labels';
import BuyItAgainLabels, {
  ACCOUNT_COMMON,
  lblBiaMainTitle,
} from '../../../../BuyItAgain/views/BuyItAgain.labels';

/**
 * This function component use for return the OrderItems
 * can be passed in the component.
 * @param otherProps - otherProps object used pass params to other component
 */

const OrderItems = ({ className, ...otherProps }) => {
  /**
   * @function return  Used to render the JSX of the component
   * @param    {[Void]} function does not accept anything.
   * @return   {[Object]} JSX of the component
   */

  const {
    item: {
      productInfo: {
        name,
        thumbnailImgPath,
        color,
        fit,
        size,
        variantNo,
        pdpUrl,
        buyItAgain,
        productId,
      },
      itemInfo: {
        linePrice,
        itemBrand,
        quantity,
        quantityCanceled,
        quantityReturned,
        listPrice,
        offerPrice,
        appeasement,
      },
      estimatedShipmentDate,
    },
    currencySymbol,
    isCanceledList,
    isShowWriteReview,
    isAppeasementEnabled,
    isNonEcom,
    isCrossBrandMerchandisingEnabled,
    onQuickViewOpenClick,
    isBIAOrderDetailsEnabled,
    isHardcodeSizeMapEnabled,
    hardcodeSizeMapConfig,
  } = otherProps;
  const idemListAndOfferPrice = listPrice === offerPrice;
  const itemQuantity = getItemQuantity(
    isCanceledList,
    quantityCanceled,
    isNonEcom,
    quantity,
    quantityReturned
  );
  const THUMBNAIL_CONFIG = 't_pdp_thumb_d,f_auto';
  const IMG_DATA_CLP = [THUMBNAIL_CONFIG, THUMBNAIL_CONFIG, THUMBNAIL_CONFIG];

  const handleAddedToBagClick = () => {
    onQuickViewOpenClick({
      colorProductId: productId,
      brandName: itemBrand,
    });
  };
  const getItemBrand = (brand) => brand && brand.toUpperCase();
  let modifiedProductId = pdpUrl?.split('/p/')[1];
  if (modifiedProductId?.includes('.png')) {
    modifiedProductId = modifiedProductId?.split('.png')[0];
  }
  const currentSiteBrandName = getBrand();
  const apiConfigObj = getAPIConfig();
  const { crossDomain, brandSpecificCrossDomain } = apiConfigObj;
  const isProductBrandOfSameDomain = currentSiteBrandName.toUpperCase() === getItemBrand(itemBrand);
  const pdpToPath = getPdpToPath(
    isProductBrandOfSameDomain,
    crossDomain,
    brandSpecificCrossDomain,
    itemBrand,
    modifiedProductId,
    pdpUrl,
    isCrossBrandMerchandisingEnabled
  );
  const pdpAsPathUrl = getPdpAsPathurl(
    isProductBrandOfSameDomain,
    crossDomain,
    brandSpecificCrossDomain,
    itemBrand,
    modifiedProductId,
    pdpUrl,
    isCrossBrandMerchandisingEnabled
  );
  const updatedSize = getUpdatedSizeName({
    size,
    primaryBrand: itemBrand,
    killSwitch: isHardcodeSizeMapEnabled,
    sizeMap: hardcodeSizeMapConfig,
  });
  const orderLabels = getLabelFromCopyOrFromTranslation(ACCOUNT_ORDERS_LABEL, OrderDetailLabels);

  return (
    <BodyCopy component="div" className={className}>
      <Row fullBleed>
        <Col colSize={{ large: 2, medium: 2, small: 2 }} className="order_item_image">
          <BodyCopy component="div">
            <Anchor to={pdpToPath} asPath={pdpAsPathUrl} noLink={false} IsSlugPathAdded>
              <DamImage
                imgData={{
                  alt: name,
                  url: thumbnailImgPath,
                }}
                isProductImage
                data-locator="order_item_image"
                imgConfigs={IMG_DATA_CLP}
                itemBrand={itemBrand}
              />
            </Anchor>
          </BodyCopy>
          <BodyCopy component="div" className="brand-logo">
            <Image
              alt={itemBrand}
              className="brand-image"
              src={getIconPath(`header__brand-tab-${itemBrand.toLowerCase()}`)}
              data-locator="order_item_brand_logo"
            />
          </BodyCopy>
        </Col>
        <Col
          colSize={{ large: 9, medium: 5, small: 4 }}
          ignoreGutter={{ small: true, medium: true, large: true }}
          offsetLeft={{ medium: 1, large: 1 }}
          className="elem-mr-MED"
        >
          <BodyCopy component="div" className="item-preview-details">
            <Row fullBleed>
              <Col colSize={{ large: 7, medium: 8, small: 4 }}>
                <Anchor
                  to={pdpToPath}
                  asPath={pdpAsPathUrl}
                  noLink={false}
                  IsSlugPathAdded
                  fontSizeVariation="large"
                  anchorVariation="primary"
                >
                  <BodyCopy
                    component="div"
                    fontSize="fs14"
                    fontWeight="extrabold"
                    fontFamily="secondary"
                  >
                    {name}
                  </BodyCopy>
                </Anchor>
              </Col>
              <Col colSize={{ large: 5, medium: 0, small: 4 }} />
            </Row>

            <BodyCopy
              component="div"
              fontSize="fs14"
              fontFamily="secondary"
              className="elem-mt-MED"
            >
              {getLabelWithDefaultValue(orderLabels, lblOtemNumber)}
              {variantNo}
            </BodyCopy>
            {color && color?.name && (
              <BodyCopy
                className="elem-mt-XXXS"
                component="div"
                fontSize="fs14"
                fontFamily="secondary"
              >
                {getLabelWithDefaultValue(orderLabels, lblOrderDetailsColor)}
                {color.name}
              </BodyCopy>
            )}
            <BodyCopy
              className="elem-mt-XXXS"
              component="div"
              fontSize="fs14"
              fontFamily="secondary"
            >
              {fit && (
                <BodyCopy
                  component="span"
                  fontSize="fs14"
                  fontFamily="secondary"
                  className="elem-mr-XL"
                >
                  {getLabelWithDefaultValue(orderLabels, lblOrderDetailsFit)}
                  {fit}
                </BodyCopy>
              )}
              {updatedSize && (
                <BodyCopy component="span" fontSize="fs14" fontFamily="secondary">
                  {getLabelWithDefaultValue(orderLabels, lblOrderDetailsSize)}
                  {updatedSize}
                </BodyCopy>
              )}
              {estimatedShipmentDate && (
                <BodyCopy
                  fontWeight="bold"
                  fontFamily="secondary"
                  fontSize="fs14"
                  color="orange.900"
                >
                  {' '}
                  {getLabelFromCopyOrFromTranslation(
                    `${CHECKOUT_BAGPAGE_LABEL}.${lblPreOrderTag}`,
                    ORDER_DETAILS_PREORDER
                  )}
                </BodyCopy>
              )}
            </BodyCopy>
          </BodyCopy>
          <BodyCopy component="div" className="elem-mt-SM itemInfo_details">
            <BodyCopy component="div" className="itemInfo_details_items">
              <BodyCopy
                component="span"
                fontSize="fs14"
                fontWeight="extrabold"
                fontFamily="secondary"
              >
                {getLabelWithDefaultValue(orderLabels, lblOrderDetailsPrice)}
              </BodyCopy>
              <BodyCopy
                component="span"
                fontSize="fs14"
                className="itemInfo_details_items_leftMargin"
                fontFamily="secondary"
              >
                {/* TO DO: - need to be clear how to define offer price */}

                {idemListAndOfferPrice}
                {currencySymbol}
                {listPrice.toFixed(2)}
              </BodyCopy>
            </BodyCopy>

            <BodyCopy component="div" className="itemInfo_details_items">
              <BodyCopy
                component="span"
                fontSize="fs14"
                fontWeight="extrabold"
                fontFamily="secondary"
              >
                {getLabelWithDefaultValue(orderLabels, lblYouPaid)}
              </BodyCopy>
              <BodyCopy
                component="span"
                fontSize="fs14"
                className="itemInfo_details_items_leftMargin"
                fontFamily="secondary"
              >
                {currencySymbol}
                {offerPrice.toFixed(2)}
              </BodyCopy>
            </BodyCopy>
            <BodyCopy component="div" className="itemInfo_details_items">
              <BodyCopy
                component="span"
                fontSize="fs14"
                fontWeight="extrabold"
                fontFamily="secondary"
              >
                {getLabelWithDefaultValue(orderLabels, lblQuantity)}
              </BodyCopy>
              <BodyCopy
                component="span"
                fontSize="fs14"
                className="itemInfo_details_items_leftMargin"
                fontFamily="secondary"
              >
                {itemQuantity}
              </BodyCopy>
            </BodyCopy>
            <BodyCopy component="div" className="itemInfo_details_items">
              <BodyCopy
                component="span"
                fontSize="fs14"
                fontWeight="extrabold"
                fontFamily="secondary"
              >
                {getLabelWithDefaultValue(orderLabels, lblSubTotal)}
              </BodyCopy>
              <BodyCopy
                component="span"
                fontSize="fs14"
                className="itemInfo_details_items_leftMargin"
                fontFamily="secondary"
              >
                {currencySymbol}
                {(linePrice || 0).toFixed(2)}
              </BodyCopy>
            </BodyCopy>
          </BodyCopy>
          <div className="action-container">
            {isShowWriteReview && (
              <BodyCopy
                component="div"
                fontSize="fs12"
                fontWeight="bold"
                fontFamily="secondary"
                className="review-button"
              >
                <Anchor
                  url={pdpUrl}
                  target="_blank"
                  fontSizeVariation="large"
                  anchorVariation="primary"
                >
                  {getLabelWithDefaultValue(orderLabels, lblWriteReview)}
                </Anchor>
              </BodyCopy>
            )}

            {buyItAgain && parseBoolean(isBIAOrderDetailsEnabled) && !isCanceledList && (
              <div>
                <Button
                  className="added-to-bag bia-atb"
                  buttonVariation="fixed-width"
                  dataLocator={getLocator('global_addtocart_Button')}
                  onClick={() => handleAddedToBagClick()}
                >
                  {getLabelFromCopyOrFromTranslation(
                    `${ACCOUNT_COMMON}.${lblBiaMainTitle}`,
                    BuyItAgainLabels
                  )}
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {appeasement && isAppeasementEnabled ? (
        <BodyCopy component="div" className="appeasment-info">
          <BodyCopy component="span" fontSize="fs12" fontWeight="bold" fontFamily="secondary">
            {`${getLabelWithDefaultValue(
              orderLabels,
              lblOrdersAdjustment
            )}, - ${currencySymbol}${Number(appeasement).toFixed(2)}`}
          </BodyCopy>
        </BodyCopy>
      ) : null}
    </BodyCopy>
  );
};
OrderItems.propTypes = {
  className: PropTypes.string,
  currencySymbol: PropTypes.string.isRequired,
  orderGroup: PropTypes.shape({}).isRequired,
  isReturnOrRefund: PropTypes.bool,
};

OrderItems.defaultProps = {
  className: '',
  isReturnOrRefund: false,
};

export default withStyles(OrderItems, styles);
export { OrderItems as OrderItemsVanilla };
